import { EmptyState, HashIcon, MessageIcon, PhoneIcon, UsersIcon } from "@rhema/ui";
import { useEffect } from "react";
import {
  Navigate,
  type RouteObject,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { AppShell, Panels } from "./app/AppShell";
import { RouteCrash } from "./app/Crash";
import { useIsDesktop } from "./app/media";
import { Root } from "./app/Root";
import { ChatInfo } from "./chats/ChatInfo";
import { Conversation, type Panel } from "./conversation/Conversation";
import { MessageLink } from "./conversation/MessageLink";
import { ImportScreen } from "./import/ImportScreen";
import { LaterScreen } from "./later/LaterScreen";
import { SearchScreen } from "./later/SearchScreen";
import { LinkCallback, LinkCallbackGate } from "./links/LinkCallback";
import { useQueravelPanel } from "./queravel/panel";
import { QueravelPanel } from "./queravel/QueravelPanel";
import {
  ForgotPasswordPage,
  OwnServerPage,
  PinResetPage,
  ResetPasswordPage,
  SignUpPage,
  VerifyEmailPage,
} from "./screens/AuthPages";
import { CallScreen } from "./screens/CallScreen";
import { CallsList } from "./screens/CallsScreen";
import { ChatByNumber } from "./screens/ChatByNumber";
import { ChatList } from "./screens/ChatList";
import { ContactScreen } from "./screens/ContactScreen";
import { ContactsList } from "./screens/ContactsScreen";
import { JoinScreen } from "./screens/JoinScreen";
import { ShareScreen } from "./screens/ShareScreen";
import { SignInPage } from "./screens/SignInScreen";
import { StatusScreen } from "./screens/StatusScreen";
import { AccountScreen, YouScreen } from "./screens/YouScreen";
import { GuestOnly, RequireSession } from "./session/gates";
import { useClientState } from "./session/session";
import { channelsOf, homeSpaceId, spaceHref } from "./spaces/data";
import { SpaceChannelsAdmin } from "./spaces/SpaceAdmin";
import {
  ChannelInfo,
  ShareAcceptScreen,
  SpaceJoinScreen,
  SpaceMembers,
  SpaceSidebar,
  SpacesHome,
} from "./spaces/SpaceViews";
import { ThreadPanel } from "./spaces/ThreadPanel";

function ChatsIndex() {
  const desktop = useIsDesktop();
  if (!desktop) return <ChatList variant="phone" />;
  return (
    <Panels
      list={<ChatList variant="desktop" />}
      main={
        <EmptyState icon={<MessageIcon size={26} />} title="Choose a chat" className="flex-1">
          Pick a conversation from the list, or start a new one. Messages are end-to-end encrypted.
        </EmptyState>
      }
    />
  );
}

function ChatRoute() {
  const { id = "" } = useParams();
  const desktop = useIsDesktop();
  const state = useClientState();
  const [params, setParams] = useSearchParams();
  const chat = state.chats[id];
  const panel = params.get("panel") === "info" ? ("info" as Panel) : null;

  const setPanel = (next: Panel | null) => {
    const p = new URLSearchParams(params);
    if (next) p.set("panel", next);
    else p.delete("panel");
    setParams(p, { replace: true });
  };

  // The person's own Queravel chat opens in the Queravel panel.
  if (id === "queravel" || chat?.kind === "assistant")
    return desktop ? <QueravelRoute /> : <Navigate to="/queravel" replace />;
  // A channel lives in its space. Links and notifications that name it as a chat go there.
  if (chat?.kind === "channel" && chat.space_id)
    return <Navigate to={spaceHref(homeSpaceId(state, chat), chat.id)} replace />;
  if (!desktop) return <Conversation chatId={id} variant="phone" />;

  const aside =
    panel === "info" && chat ? (
      <ChatInfo key={id} chatId={id} variant="panel" onClose={() => setPanel(null)} />
    ) : undefined;

  return (
    <Panels
      list={<ChatList variant="desktop" selectedId={id} />}
      main={<Conversation key={id} chatId={id} variant="desktop" panel={panel} onPanel={setPanel} />}
      aside={aside}
    />
  );
}

function ChatInfoRoute() {
  const { id = "" } = useParams();
  const desktop = useIsDesktop();
  const [params] = useSearchParams();
  // Add people from a menu keeps asking for the picker when the info opens beside the chat.
  if (desktop)
    return <Navigate to={`/chats/${id}?panel=info${params.get("add") === "1" ? "&add=1" : ""}`} replace />;
  return <ChatInfo chatId={id} variant="phone" />;
}

function CallsIndex() {
  const desktop = useIsDesktop();
  if (!desktop) return <CallsList variant="phone" />;
  return (
    <Panels
      list={<CallsList variant="desktop" />}
      main={
        <EmptyState icon={<PhoneIcon size={26} />} title="Your calls" className="flex-1">
          Call someone back from the list, or start a call from any chat. Calls are end-to-end encrypted.
        </EmptyState>
      }
    />
  );
}

function CallRoute() {
  const { id = "" } = useParams();
  const desktop = useIsDesktop();
  const screen = <CallScreen chatId={id} />;
  if (!desktop) return screen;
  return <Panels list={<CallsList variant="desktop" />} main={screen} />;
}

/**
 * /queravel opens the Queravel panel. On desktop it opens beside the screen the person was on, so
 * the address goes back there. On phones the panel is this screen. ?c= opens one conversation and
 * ?new=1 starts a new one.
 */
function QueravelRoute({ history = false }: { history?: boolean }) {
  const desktop = useIsDesktop();
  const panel = useQueravelPanel();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const conversation = params.get("c");
  const fresh = params.get("new") === "1";
  // biome-ignore lint/correctness/useExhaustiveDependencies: opens once per address
  useEffect(() => {
    panel.show({
      ...(conversation ? { conversation } : {}),
      ...(fresh ? { fresh } : {}),
      ...(history ? { view: "history" as const } : {}),
    });
    if (desktop) navigate(panel.lastPlace(), { replace: true });
  }, [desktop, conversation, fresh, history]);
  if (desktop) return null;
  return <QueravelPanel variant="phone" />;
}

function ContactsIndex() {
  const desktop = useIsDesktop();
  if (!desktop) return <ContactsList variant="phone" />;
  return (
    <Panels
      list={<ContactsList variant="desktop" />}
      main={
        <EmptyState icon={<UsersIcon size={26} />} title="Your contacts" className="flex-1">
          Choose someone to see their details and safety number.
        </EmptyState>
      }
    />
  );
}

function ContactRoute() {
  const { id = "" } = useParams();
  const desktop = useIsDesktop();
  if (!desktop) return <ContactScreen key={id} personId={id} variant="phone" />;
  return (
    <Panels
      list={<ContactsList variant="desktop" selectedId={id} />}
      main={<ContactScreen key={id} personId={id} variant="page" />}
    />
  );
}

function YouRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<YouScreen variant="desktop" />} /> : <YouScreen variant="phone" />;
}

function AccountRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<AccountScreen variant="desktop" />} /> : <AccountScreen variant="phone" />;
}

function JoinRoute() {
  const { code = "" } = useParams();
  const desktop = useIsDesktop();
  const screen = <JoinScreen code={code} />;
  return desktop ? <Panels main={screen} /> : screen;
}

/** A link to a message, which goes on to the chat or thread it is in. */
function MessageLinkRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<MessageLink />} /> : <MessageLink />;
}

function ChatByNumberRoute() {
  const { digits = "" } = useParams();
  const desktop = useIsDesktop();
  const screen = <ChatByNumber key={digits} digits={digits} />;
  return desktop ? <Panels main={screen} /> : screen;
}

function ShareRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<ShareScreen />} /> : <ShareScreen />;
}

function SpacesIndex() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<SpacesHome variant="desktop" />} /> : <SpacesHome variant="phone" />;
}

function SpaceJoinRoute() {
  const { code = "" } = useParams();
  const desktop = useIsDesktop();
  const screen = <SpaceJoinScreen code={code} />;
  return desktop ? <Panels main={screen} /> : screen;
}

function ShareRouteForSpaces() {
  const { code = "" } = useParams();
  const desktop = useIsDesktop();
  const screen = <ShareAcceptScreen code={code} />;
  return desktop ? <Panels main={screen} /> : screen;
}

function SpaceRoute() {
  const { spaceId = "" } = useParams();
  const desktop = useIsDesktop();
  const state = useClientState();
  const { search } = useLocation();
  if (!desktop) return <SpaceSidebar spaceId={spaceId} variant="phone" />;
  // On desktop a space opens on its first channel, which is #general unless you left it. What the
  // address asks for, such as the invite sheet, goes with it.
  const first =
    channelsOf(state, spaceId).find((c) => c.title === "general") ?? channelsOf(state, spaceId)[0];
  if (first) return <Navigate to={`${spaceHref(spaceId, first.id)}${search}`} replace />;
  return (
    <Panels
      list={<SpaceSidebar spaceId={spaceId} variant="desktop" />}
      main={
        <EmptyState icon={<HashIcon size={26} />} title="No channels yet" className="flex-1">
          Browse the space's channels, or create one.
        </EmptyState>
      }
    />
  );
}

function SpaceMembersRoute() {
  const { spaceId = "" } = useParams();
  const desktop = useIsDesktop();
  if (!desktop) return <SpaceMembers spaceId={spaceId} variant="phone" />;
  return (
    <Panels
      list={<SpaceSidebar spaceId={spaceId} variant="desktop" />}
      main={<SpaceMembers spaceId={spaceId} variant="desktop" />}
    />
  );
}

function SpaceManageRoute() {
  const { spaceId = "" } = useParams();
  const desktop = useIsDesktop();
  if (!desktop) return <SpaceChannelsAdmin spaceId={spaceId} variant="phone" />;
  return (
    <Panels
      list={<SpaceSidebar spaceId={spaceId} variant="desktop" />}
      main={<SpaceChannelsAdmin spaceId={spaceId} variant="desktop" />}
    />
  );
}

function ChannelRoute() {
  const { spaceId = "", chatId = "" } = useParams();
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const thread = params.get("thread");
  const panel = params.get("panel") === "info" ? "info" : null;
  const setQuery = (key: "thread" | "panel", value: string | null) => {
    const p = new URLSearchParams(params);
    p.delete("thread");
    p.delete("panel");
    if (value) p.set(key, value);
    setParams(p, { replace: true });
  };
  if (!desktop) {
    return (
      <Conversation
        key={chatId}
        chatId={chatId}
        variant="phone"
        onThread={(rootId) => navigate(`${spaceHref(spaceId, chatId)}/thread/${rootId}`)}
      />
    );
  }
  const aside = thread ? (
    <ThreadPanel
      key={thread}
      chatId={chatId}
      rootId={thread}
      variant="panel"
      onClose={() => setQuery("thread", null)}
    />
  ) : panel ? (
    <ChannelInfo
      key={chatId}
      chatId={chatId}
      spaceId={spaceId}
      variant="panel"
      onClose={() => setQuery("panel", null)}
    />
  ) : undefined;
  return (
    <Panels
      list={<SpaceSidebar spaceId={spaceId} selectedId={chatId} variant="desktop" />}
      main={
        <Conversation
          key={chatId}
          chatId={chatId}
          variant="desktop"
          panel={panel}
          onPanel={(next) => setQuery("panel", next)}
          onThread={(rootId) => setQuery("thread", rootId)}
        />
      }
      aside={aside}
    />
  );
}

function ChannelInfoRoute() {
  const { spaceId = "", chatId = "" } = useParams();
  const desktop = useIsDesktop();
  const [params] = useSearchParams();
  if (desktop)
    return (
      <Navigate
        to={`${spaceHref(spaceId, chatId)}?panel=info${params.get("add") === "1" ? "&add=1" : ""}`}
        replace
      />
    );
  return <ChannelInfo chatId={chatId} spaceId={spaceId} variant="phone" />;
}

function ThreadRoute() {
  const { spaceId = "", chatId = "", rootId = "" } = useParams();
  const desktop = useIsDesktop();
  if (desktop) return <Navigate to={`${spaceHref(spaceId, chatId)}?thread=${rootId}`} replace />;
  return (
    <ThreadPanel chatId={chatId} rootId={rootId} variant="phone" backHref={spaceHref(spaceId, chatId)} />
  );
}

function LaterRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<LaterScreen variant="desktop" />} /> : <LaterScreen variant="phone" />;
}

function SearchRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<SearchScreen variant="desktop" />} /> : <SearchScreen variant="phone" />;
}

function ImportRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<ImportScreen />} /> : <ImportScreen />;
}

function LinkCallbackRoute() {
  const desktop = useIsDesktop();
  return desktop ? <Panels main={<LinkCallback />} /> : <LinkCallback />;
}

function NotFound() {
  return (
    <EmptyState title="Page not found" className="flex-1">
      This page does not exist. Go back to your chats.
    </EmptyState>
  );
}

export const routes: RouteObject[] = [
  {
    element: <Root />,
    errorElement: <RouteCrash />,
    children: [
      { index: true, element: <Navigate to="/chats" replace /> },
      { path: "status", element: <StatusScreen /> },
      { path: "verify-email", element: <VerifyEmailPage /> },
      { path: "reset-password", element: <ResetPasswordPage /> },
      // SBI links PIN reset emails here.
      { path: "phone/pin-reset", element: <PinResetPage /> },
      { path: "own-server", element: <OwnServerPage /> },
      // SBI returns every social link here. A link from the desktop or mobile app goes back to the
      // app before the session gate, because the system browser has no Rhema session.
      {
        path: "link/callback",
        element: <LinkCallbackGate />,
        children: [
          {
            element: <RequireSession />,
            children: [
              { element: <AppShell />, children: [{ index: true, element: <LinkCallbackRoute /> }] },
            ],
          },
        ],
      },
      {
        element: <GuestOnly />,
        children: [
          { path: "sign-in", element: <SignInPage key="sign-in" /> },
          // A new phone number makes an account, so signing up is sign-in with other words.
          { path: "sign-up", element: <SignInPage key="sign-up" mode="signup" /> },
          { path: "sign-up/email", element: <SignUpPage /> },
          { path: "forgot-password", element: <ForgotPasswordPage /> },
        ],
      },
      {
        element: <RequireSession />,
        children: [
          {
            element: <AppShell />,
            children: [
              { path: "chats", element: <ChatsIndex /> },
              { path: "chats/:id", element: <ChatRoute /> },
              { path: "chats/:id/info", element: <ChatInfoRoute /> },
              { path: "m/:chatId/:messageId", element: <MessageLinkRoute /> },
              { path: "calls", element: <CallsIndex /> },
              { path: "calls/:id", element: <CallRoute /> },
              { path: "queravel", element: <QueravelRoute /> },
              { path: "queravel/conversations", element: <QueravelRoute history /> },
              { path: "contacts", element: <ContactsIndex /> },
              { path: "contacts/:id", element: <ContactRoute /> },
              { path: "join/:code", element: <JoinRoute /> },
              { path: "chat/:digits", element: <ChatByNumberRoute /> },
              { path: "you", element: <YouRoute /> },
              { path: "you/account", element: <AccountRoute /> },
              { path: "share", element: <ShareRoute /> },
              { path: "spaces", element: <SpacesIndex /> },
              { path: "spaces/join/:code", element: <SpaceJoinRoute /> },
              { path: "spaces/share/:code", element: <ShareRouteForSpaces /> },
              { path: "spaces/:spaceId", element: <SpaceRoute /> },
              { path: "spaces/:spaceId/members", element: <SpaceMembersRoute /> },
              { path: "spaces/:spaceId/manage", element: <SpaceManageRoute /> },
              { path: "spaces/:spaceId/:chatId", element: <ChannelRoute /> },
              { path: "spaces/:spaceId/:chatId/info", element: <ChannelInfoRoute /> },
              { path: "spaces/:spaceId/:chatId/thread/:rootId", element: <ThreadRoute /> },
              { path: "later", element: <LaterRoute /> },
              { path: "search", element: <SearchRoute /> },
              { path: "import", element: <ImportRoute /> },
              { path: "*", element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];
